import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Center,
  Input,
  Button,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useTwoFactorStore } from '../store/twoFactorStore';
import { useUserStore } from '../store/userStore';
import { twoFactorApi } from '../lib/api/twoFactorApi';
import {useModernToast} from './ModernToast';

interface TwoFactorVerificationProps {
  onVerificationComplete: () => void;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({ onVerificationComplete }) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasRequestedCode = useRef(false);
  const [typeOf2FA, setTypeOf2FA] = useState<'email' | 'phone'>('email');
  
  const { user } = useUserStore();
  const { set2FAVerified, setTwoFactorToken } = useTwoFactorStore();
  const toast = useModernToast();

  useEffect(() => {
    // Automatically request 2FA code when component mounts (only once)
    if (!hasRequestedCode.current) {
      hasRequestedCode.current = true;
      request2FACode();
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      const typeOf2FA = user.user_settings.two_fa_type === 'sms' ? 'phone' : 'email';
      if (typeOf2FA) {
        setTypeOf2FA(typeOf2FA as 'email' | 'phone');
      }
    }
  }, [user?.id]);

  const request2FACode = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      const response = await twoFactorApi.request2FA(user.id.toString());
      if (response.success) {
        toast.success('2FA code sent! Please check your device and enter the 6-digit code below.');
      } else {
        // toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to send 2FA code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow digits and limit to 6 characters
    if (/^\d{0,6}$/.test(value)) {
      setCode(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);

    try {
      const response = await twoFactorApi.verify2FA(user.id.toString(), code);

      if (response.success) {
        set2FAVerified(true);
        if (response.token) {
          setTwoFactorToken(response.token);
        }
        toast.success('2FA verification successful!');
        
        // Call the completion callback
        setTimeout(() => {
          onVerificationComplete();
        }, 1000);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Error: Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    setCode('');
    hasRequestedCode.current = false; // Reset the flag to allow resending
    request2FACode();
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      display="flex"
      flexDirection="column"
      position="absolute"
      top="0"
      left="0"
      bgGradient="linear(to-b, black, #002e0b)"
      color="white"
      fontFamily="'Exo 2', sans-serif"
    >
      <Container maxW="600px" pt={32} flex="1 0 auto">
        <Center minH="60vh">
          <VStack gap={8} width="full">
            <VStack gap={3} textAlign="center" w="full">
              <Box w="full" maxW="600px" mx="auto">
                <Heading
                  as="h1"
                  size="xl"
                  fontFamily="Audiowide"
                  fontWeight="bold"
                  color="white"
                >
                  Two-Factor Authentication
                </Heading>
                <Text 
                  color="white"
                  fontSize="lg"
                  opacity={0.85}
                  mt={3}
                >
                  We've sent a 6-digit code to your {typeOf2FA}. <br />Please verify your identity to continue
                </Text>
              </Box>
            </VStack>
            
            <Box
              bg="black"
              p={8}
              rounded="lg"
              w="full"
              textAlign="center"
              boxShadow="xl"
            >
              <form onSubmit={handleSubmit}>
                <VStack gap={6}>
                  <FormControl>
                    <FormLabel 
                      htmlFor="code" 
                      color="white" 
                      fontSize="md" 
                      fontWeight="medium"
                    >
                      Enter 6-digit code
                    </FormLabel>
                    <Input
                      id="code"
                      type="text"
                      value={code}
                      onChange={handleCodeChange}
                      // placeholder="000000"
                      size="lg"
                      textAlign="center"
                      fontSize="2xl"
                      letterSpacing="wider"
                      maxLength={6}
                      disabled={isLoading}
                      autoComplete="one-time-code"
                      bg="gray.800"
                      borderColor="gray.600"
                      color="white"
                      _placeholder={{ color: 'gray.400' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                      }}
                      _disabled={{
                        bg: 'gray.700',
                        color: 'gray.400',
                        cursor: 'not-allowed',
                      }}
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    w="full"
                    variant="dominus"
                    py={4}
                    fontSize="xl"
                    disabled={isLoading || code.length !== 6}
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleResendCode}
                    variant="ghost"
                    size="sm"
                    disabled={isLoading}
                    _hover={{ bg: 'whiteAlpha.200' }}
                  >
                    Resend Code
                  </Button>
                </VStack>
              </form>
            </Box>
          </VStack>
        </Center>
      </Container>

    </Box>
  );
};

export default TwoFactorVerification;
